// React
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

// Lodash
import _ from 'lodash';

// I18n
import { Translate } from 'react-redux-i18n';

// React Spectrum Components
import {
  Flex,
  View,
  Button,
  Divider,
} from '@adobe/react-spectrum';

// Icons
import FilterIcon from '@spectrum-icons/workflow/Properties';

// React Router
import { withRouter } from '../withRouter';

// Actions
import { clearAllFilters } from '../../actions/filterActions';

// Styles
import '../../styles/FilterContainer.scss';
import LocationFilter from './LocationFilter';
import MultiSelectFilter from './MultiSelectFilter';
import ToggleFilter from './ToggleFilter';
import SpecializedToggleFilter from './SpecializedToggleFilter';
import Accordion from '../global/Accordion/Accordion';
import PartnerAwardsToggleFilter from './PartnerAwardsToggleFilter';
import ProgramTypeConfig from '../../util/programTypeConfig';
import { INITIAL_STATE } from '../../reducers/filterReducer';

const FilterContainer = ({
  appliedFilters,
  clearAllFiltersAction,
  router,
  programPath,
}) => {
  const applicableFilters = ProgramTypeConfig[programPath].apllicableFilters;
  // Check if certain filters are already applied
  const hiddenFiltersAlreadyApplied = (
    _.has(appliedFilters, 'industry')
    || _.has(appliedFilters, 'level')
    || _.has(appliedFilters, 'specialized')
  );

  const clearDisabled = _.isEqual({
    locationType: 'all',
  }, appliedFilters);

  return (
    <View UNSAFE_className="filterContainer">
      <View gridArea="filters" id="filter-section">
        <Flex alignItems="center" gap="7px" justifyContent="space-between">
          <Translate value="partner_finder.filters.filters" className="filterLabel" />
          <Button variant="secondary" isQuiet isDisabled={clearDisabled} onPress={() => clearAllFiltersAction(router, programPath)} id="clear-all-filters-button" data-test-id="clear-all-filters-button">
            <Translate value="partner_finder.filters.clear_all" />
          </Button>
        </Flex>
        <Divider size="S" marginBottom={10} marginTop={10} />
      </View>
      {applicableFilters.location && (
        <>
          <Accordion title="Locations" borderStyle="none"><LocationFilter /></Accordion>
          <Divider size="S" />
        </>
      )}
      {applicableFilters.applications && (
        <>
          <Accordion
            title="Applications"
            borderStyle="none"
            metadata={appliedFilters.solution?.length && <div className="setFilterIndicator">{appliedFilters.solution.length}</div>}
          >
            <MultiSelectFilter
              filterName="solution"
              nameTranslationPath="partner_finder.filters.products_label"
              infoTranslationPath="partner_finder.filters.products_info"
            />
          </Accordion>
          <Divider size="S" />
        </>
      )}
      {applicableFilters.partnerSolutions && (
        <>
          <Accordion
            title="Partner Solutions"
            borderStyle="none"
            metadata={appliedFilters['accredited-solutions'] && <div className="setFilterIndicator">{1}</div>}
          >
            <ToggleFilter />
          </Accordion>
          <Divider size="S" />
        </>
      )}
      {applicableFilters.initiatives && (
        <>
          <Accordion
            title="Initiatives"
            borderStyle="none"
            metadata={appliedFilters.initiatives?.length && <div className="setFilterIndicator">{appliedFilters.initiatives.length}</div>}
          >
            <MultiSelectFilter
              filterName="initiatives"
              nameTranslationPath="partner_finder.filters.industries_label"
              infoTranslationPath="partner_finder.filters.industries_info"
            />
          </Accordion>
          <Divider size="S" />
        </>
      )}
      {applicableFilters.industries && (
        <>
          <Accordion
            title="Industries"
            borderStyle="none"
            metadata={appliedFilters.industry?.length && <div className="setFilterIndicator">{appliedFilters.industry.length}</div>}
          >
            <MultiSelectFilter
              filterName="industry"
              nameTranslationPath="partner_finder.filters.industries_label"
              infoTranslationPath="partner_finder.filters.industries_info"
            />
          </Accordion>
          <Divider size="S" />
        </>
      )}
      {applicableFilters.partnerLevels && (
        <>
          <Accordion
            title="Partner levels"
            borderStyle="none"
            metadata={appliedFilters.level?.length && <div className="setFilterIndicator">{appliedFilters.level.length}</div>}
          >
            <MultiSelectFilter
              filterName="level"
              nameTranslationPath="partner_finder.filters.partner_level_label"
              infoTranslationPath="partner_finder.filters.partner_level_info"
            />
          </Accordion>
          <Divider size="S" />
        </>
      )}
      {applicableFilters.specialized && (
        <>
          <Accordion
            title="Specialized"
            borderStyle="none"
            isOpen
            metadata={appliedFilters.specialized && <div className="setFilterIndicator">{1}</div>}
          >
            <SpecializedToggleFilter />
          </Accordion>
          <Divider size="S" />
        </>
      )}
      {applicableFilters.partnerAwards && (
        <>
          <Accordion
            title="Partner Awards"
            borderStyle="none"
            metadata={appliedFilters.badges && <div className="setFilterIndicator">{1}</div>}

          >
            <PartnerAwardsToggleFilter />
          </Accordion>
          <Divider size="S" />
        </>
      )}
      <div className="learnPartner">
        <h3>
          <a href={programPath === 'mxp' ? window.directoryConfig.findTppAdobePartner : window.directoryConfig.findSppAdobePartner} target="_blank" rel="noreferrer">Learn </a>
          {ProgramTypeConfig[programPath].text}
        </h3>
      </div>

    </View>
  );
};

FilterContainer.propTypes = {
  appliedFilters: PropTypes.shape({
    location: PropTypes.shape({
      geo: PropTypes.string,
      country: PropTypes.string,
    }),
  }),
  clearAllFiltersAction: PropTypes.func.isRequired,
  router: PropTypes.shape({}).isRequired,
};

FilterContainer.defaultProps = {
  appliedFilters: {},
};

const mapStateToProps = (state) => ({
  appliedFilters: state.filter.selected,
  totalResults: state.results.totalResults,
  programPath: state.facets.programPath,
});

const mapDispatchToProps = {
  clearAllFiltersAction: clearAllFilters,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(FilterContainer));
